import { useCallback, useContext, useEffect, useState } from "react";
import { Grid, TextField, Button, Typography, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { LoginService } from "./api/LoginService";
import { LoginFormSchema, LoginRequest, LoginResponse } from "./api/LoginForm";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PageTitle } from "../../components/common/PageTitle";
import Constants from "../../utils/Constants";
import { useCookies } from "react-cookie";
import { ApplicationProtectedRouting } from "../../Routing";
import { Loading } from "../common/Loading";

const Login = () => {
  const navigate = useNavigate();
  const PAGE_NAME = "ログイン";
  const MFA_TWOSTEP = "I-ADM-003-008"; //MFA認証あり
  const MFA_TOP = "I-ADM-003-009"; //MFA認証なし

  // クッキー情報
  const [cookies, setCookie, removeCookie] = useCookies([
    "userId", //ユーザID
    "userContract", //ユーザ名
    "date", //最終ログイン日時
    "authList", //権限CDリスト
    "token", //トークン
    "maxage", //期限日時
  ]);

  const deleteCookie = useCallback(() => {
    removeCookie("userId");
    removeCookie("userContract");
    removeCookie("date");
    removeCookie("authList");
    removeCookie("token");
    removeCookie("maxage");
  }, [removeCookie]);

  const setCookieData = useCallback((userInfo: LoginResponse) => {

    // ユーザ情報取得
    const objectJSON = JSON.parse(userInfo.json);

    // 使用期限
    let maxagedate = new Date();
    maxagedate.setSeconds(maxagedate.getSeconds() + Constants.SESSION_MAX_ARG);

    // ユーザーID
    setCookie("userId", objectJSON.id)
    // ユーザ名
    setCookie("userContract", objectJSON.fullName);
    // 日付 yyyyMMddHHmmssSSS
    setCookie("date", userInfo.lastLoginDate);
    // 権限CDリスト
    setCookie("authList", userInfo.authList);
    // トークン
    setCookie("token", userInfo.token);
    // 有効期限
    setCookie("maxage", maxagedate);
  }, [deleteCookie, setCookie]);

  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);

  // エラーメッセージ
  const [errorMessage, setErrorMessage] = useState("");
  // リクエスト情報
  const [request, setRequest] = useState<LoginRequest>({
    username: "",
    password: "",
  });
  // レスポンス情報
  const [loginResponse, setResponse] = useState<LoginResponse>({
    status: "",
    code: "",
    displayMessage: "",
    errorMessages: "",
    json: "",
    lastLoginDate: "",
    token: "",
    authList: "",
  });

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // 暫定 Cookie削除対応
    deleteCookie();
    if (loginResponse.code === MFA_TOP) {
      setCookieData(loginResponse)
    }
  }, [setCookieData, loginResponse, deleteCookie]);
  
  // 入力情報:チェンジイベント
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequest({ ...request, [e.target.name]: e.target.value });
  };

  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.username) {
      setError("username", { message: error.username });
    }
    if (error.password) {
      setError("password", { message: error.password });
    }
  };

  // ログインボタン押下
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: yupResolver(LoginFormSchema),
    reValidateMode: "onSubmit",
  });
  const onSubmit: SubmitHandler<LoginRequest> = (request) => {
    const service = new LoginService();
    setIsLoading(true);
    service
      .login(request)
      .then((response: LoginResponse) => {
        setResponse(response);
        if (Constants.ERR_STATUS_OK === response.status) {
          if (response.code === MFA_TWOSTEP) {
            navigate("/twostep_auth");
          } else if (response.code === MFA_TOP) {
            navigate(ApplicationProtectedRouting.tenant.path());
          } else {
            // 想定外エラー
            setErrorMessage(Constants.E_MSG_SYSTEM_ERR);
          }
        } else if (Constants.ERR_STATUS_INVALID === response.status) {
          // 入力違反
          setBackendValidMessage(response.errorMessages);
        } else {
          // エラー
          setErrorMessage(response.displayMessage);
        }
      })
      .catch((err) => {
        // 異常エラー
        setErrorMessage(Constants.E_MSG_SYSTEM_ERR);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ mt: 10, px: 4 }}
    >
      <PageTitle title={PAGE_NAME} />

      <Grid
        sx={{
          width: {
            xs: "100%",
            sm: "500px",
          },
        }}
      >
        <Typography variant="h1" align="center" sx={{ fontSize: 30 }}>
          MDM契約管理システム
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ユーザ名 */}
          <TextField
            label="メールアドレス"
            fullWidth
            size="small"
            margin="normal"
            autoComplete="email"
            autoFocus
            InputLabelProps={{ shrink: true }}
            {...register("username")}
            error={"username" in errors}
            helperText={errors.username?.message}
          />
          {/* パスワード */}
          <TextField
            label="パスワード"
            fullWidth
            size="small"
            margin="normal"
            type="password"
            autoComplete="current-password"
            InputLabelProps={{ shrink: true }}
            {...register("password")}
            error={"password" in errors}
            helperText={errors.password?.message}
          />

          {/*エラーメッセージ*/}
          <Typography component="p" sx={{ fontSize: 16, mt: 1 }}>
            {errorMessage}
          </Typography>

          {/* ログインボタン */}
          <Button type="submit" fullWidth variant="contained" sx={{ my: 3 }}>
            {PAGE_NAME}
          </Button>
        </form>

        {/* <Typography component="p" align="center" sx={{ fontSize: 16 }}>
          パスワードを忘れた方は<Link href="/password_reminder">こちら</Link>
        </Typography> */}
      </Grid>

      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </Grid>
  );
};
export default Login;
